import {
    Autocomplete,
    Box,
    Card,
    Stack,
    Typography,
    FormControl,
    Chip,
    FormLabel,
} from '@mui/joy'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { useContext, useState } from 'react'
import { useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import MessageList from 'components/MessageList/MessageList'

import {
    useIntegrationList,
    useIntegrationMessages,
} from 'api/chat-api-integration'
import { uniq } from 'lodash'
import { chatApiList } from 'components/ChatApisPreview/ChatApisPreview'
import { MessagesRequestProps } from 'types/dataManagement'
import Close from '@mui/icons-material/Close'
import { AuthContext } from 'context/Auth'
import { useGeneral } from 'context/General'

type Props = {}

type SourceItem = {
    key: string
    label: string
    icon?: string
}

const MessagesPage = (props: Props) => {
    const queryClient = useQueryClient()

    const { user } = useContext(AuthContext)

    if (!user?.access) {
        throw new Error('User has no access and cannot fetch messages.')
    }

    const workspaceId = user?.access?.workspaceId
    const userId = user?.access?.internalUserId

    const { from, to, setFromGeneral, setToGeneral } = useGeneral()

    const {
        data: integrationList,
        isLoading,
        isError,
    } = useIntegrationList({
        workspaceId: workspaceId,
        userId: userId,
        limit: 25,
        page: 1,
    })

    const [searchTags, setSearchTags] = useState<string[]>([])

    // const [from, setFrom] = useState(
    //     dayjs().add(-92, 'days').startOf('day').unix()
    // )
    // const [to, setTo] = useState(dayjs().endOf('day').unix())
    const [dateFormat, setDateFormat] = useState('MMM DD, YYYY')
    const [searchQuery, setSearchQuery] = useState('')
    const [activeSourceTab, setActiveSourceTab] = useState<any>([
        {
            key: 'all',
            label: 'All',
        },
    ])

    const [activeNameTab, setActiveNameTab] = useState<any>([
        {
            key: 'all',
            label: 'All',
        },
    ])

    const [filters, setFilters] = useState<string[]>([])

    const filteredIntegrationListBySource = integrationList?.map(
        (integration) => integration.source
    )
    const uniqIntegrationsBySource = uniq(filteredIntegrationListBySource)

    const mappedSources = uniqIntegrationsBySource.map((item) => {
        let el = chatApiList.find((j) => j.key === item)

        return {
            key: item,
            label: el?.label ?? 'Unknown',
            icon: el?.icon,
        }
    })

    // const filteredIntegrationListByName = integrationList?.map(
    //     (integration) => integration.name
    // )
    // const uniqIntegrationsByName = uniq(filteredIntegrationListByName)

    const mappedNames = integrationList?.map((item) => {
        let el = chatApiList.find((j) => j.key === item.source)

        return {
            key: item.source,
            label: item?.name ?? 'Unknown',
            icon: el?.icon,
        }
    })

    const queryForMessages = (sources: any[], names: any[], text: string) => {
        const hasAllSources = sources.some((s) => s.key === 'all')
        const hasAllNames = names.some((n) => n.key === 'all')

        const arr: string[] = []

        // Sources
        if (!hasAllSources) {
            const validSources = sources
                .map((s) => s.key?.trim())
                .filter((s) => s && s.length > 0)

            if (validSources.length === 1) {
                arr.push(`source:${validSources[0]}`)
            } else if (validSources.length > 1) {
                const sourceGroup = validSources
                    .map((s) => `source:${s}`)
                    .join(' OR ')
                arr.push(`(${sourceGroup})`)
            }
        }

        // Names
        if (!hasAllNames) {
            const validNames = names
                .map((n) => n.label?.trim())
                .filter((n) => n && n.length > 0)
            if (validNames.length === 1) {
                arr.push(validNames[0])
            } else if (validNames.length > 1) {
                arr.push(`(${validNames.join(' OR ')})`)
            }
        }

        // Search Field
        if (searchTags.length > 0) {
            arr.push(searchTags.join(' AND '))
        }

        return arr.length > 0 ? arr.join(' AND ') : '*'
    }

    const query = queryForMessages(activeSourceTab, activeNameTab, searchQuery)

    const objectsMessagesReqProps: MessagesRequestProps = {
        filters: filters,
        query: query,
        workspaceId: workspaceId,
        limit: 25,
        startDateTime: from,
        endDateTime: to,
    }

    const {
        data: messagesList,
        isLoading: isLoadingMessages,
        isIdle,
        isFetching,
        isError: isErrorMessages,
    } = useIntegrationMessages(objectsMessagesReqProps)

    const handleOptions = (
        setState: (state: any) => void,
        event: React.SyntheticEvent,
        value: SourceItem[],
        reason: any,
        details: any
    ) => {
        if (
            details?.option.key === 'all' ||
            (reason === 'removeOption' && value.length === 0) ||
            reason === 'clear'
        ) {
            setState([{ key: 'all', label: 'All', icon: null }])
            return
        }

        if (reason === 'selectOption' && details?.option.key !== 'all') {
            setState((prev: any) => {
                let copy = prev.filter((el: any) => el.key !== 'all')
                return [...copy, details.option]
            })
            return
        }

        setState(value)
    }

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && searchQuery.trim()) {
            event.preventDefault()

            const newTag = searchQuery.trim()

            setSearchTags((prev: any) => [...prev, newTag])

            setSearchQuery('')
        }
    }

    if (isError) return <div>Error Connection</div>
    if (isErrorMessages) return <div>Error Messages</div>

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    my: 1,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'start',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Typography level="h2">
                    Messages{' '}
                    {messagesList?.itemsCount
                        ? `[${messagesList?.itemsCount}]`
                        : null}
                </Typography>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        marginTop: '12px',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            closeOnSelect={true}
                            label="From"
                            value={dayjs.unix(
                                from ?? dayjs().startOf('day').unix()
                            )}
                            format={dateFormat}
                            onChange={(newValue) => {
                                // setFrom(newValue?.unix() ?? dayjs().unix())
                                setFromGeneral(
                                    newValue?.unix() ?? dayjs().unix()
                                )

                                return
                            }}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="To"
                            closeOnSelect={true}
                            value={dayjs.unix(
                                to ?? dayjs().startOf('day').unix()
                            )}
                            format={dateFormat}
                            onChange={(newValue) => {
                                // setTo(newValue?.unix() ?? dayjs().unix())
                                setToGeneral(newValue?.unix() ?? dayjs().unix())

                                return
                            }}
                        />
                    </LocalizationProvider>
                </Stack>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    marginBottom: 1,
                    marginTop: 1,
                    flexWrap: 'wrap',
                    alignItems: 'start',
                }}
            >
                <Box
                    sx={{
                        maxWidth: '400px',
                    }}
                >
                    <FormControl size="lg">
                        <FormLabel>Messenger:</FormLabel>
                        <Autocomplete
                            multiple
                            options={[
                                {
                                    key: 'all',
                                    label: 'All',
                                    icon: null,
                                },
                                ...mappedSources,
                            ]}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, v, r, d) =>
                                handleOptions(setActiveSourceTab, e, v, r, d)
                            }
                            value={activeSourceTab}
                            isOptionEqualToValue={(opt, val) =>
                                opt.key === val.key
                            }
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Card
                                        variant="plain"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 1,
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            backgroundColor: selected
                                                ? 'primary.solidBg'
                                                : 'transparent',
                                            '&:hover': {
                                                background: 'primary.solidBg',
                                            },
                                        }}
                                    >
                                        {option.icon ? (
                                            <img
                                                src={option.icon}
                                                alt={option.label}
                                                width={20}
                                                height={20}
                                            />
                                        ) : null}

                                        <Typography level="title-md">
                                            {option.label}
                                        </Typography>
                                    </Card>
                                </li>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const tagProps = getTagProps({ index })

                                    return (
                                        <Chip
                                            {...tagProps}
                                            key={option.key}
                                            variant="soft"
                                            color="primary"
                                            startDecorator={
                                                option.icon && (
                                                    <img
                                                        src={option.icon}
                                                        alt={option.label}
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                )
                                            }
                                            endDecorator={<Close />}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingRight: 0.5,
                                                gap: 0.5,
                                                borderRadius: 'md',
                                                fontSize: 'sm',
                                            }}
                                        >
                                            {option.label}
                                        </Chip>
                                    )
                                })
                            }
                            slotProps={{
                                listbox: {
                                    sx: {
                                        '& .Mui-disabled': {
                                            opacity: 0.4,
                                            color: 'red',
                                            cursor: 'not-allowed',
                                        },
                                    },
                                },
                            }}
                        />
                    </FormControl>
                </Box>
                <Box>
                    <FormControl size="lg">
                        <FormLabel>Account:</FormLabel>
                        <Box
                            sx={{
                                width: 'fit-content',
                            }}
                        >
                            <Autocomplete
                                multiple
                                options={[
                                    {
                                        key: 'all',
                                        label: 'All',
                                        icon: null,
                                    },
                                    ...(mappedNames ?? []),
                                ]}
                                getOptionLabel={(option) => option.label}
                                onChange={(e, v, r, d) =>
                                    handleOptions(setActiveNameTab, e, v, r, d)
                                }
                                value={activeNameTab}
                                isOptionEqualToValue={(opt, val) =>
                                    opt.label === val.label
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Card
                                            variant="plain"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: 1,
                                                padding: '12px 16px',
                                                cursor: 'pointer',
                                                backgroundColor: selected
                                                    ? 'primary.solidBg'
                                                    : 'transparent',
                                                '&:hover': {
                                                    background:
                                                        'primary.solidBg',
                                                },
                                            }}
                                        >
                                            <Typography level="title-md">
                                                {option.label}
                                            </Typography>
                                        </Card>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const tagProps = getTagProps({
                                            index,
                                        })

                                        return (
                                            <Chip
                                                {...tagProps}
                                                key={option.label}
                                                variant="soft"
                                                color="primary"
                                                endDecorator={<Close />}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    paddingRight: 0.5,
                                                    gap: 0.5,
                                                    borderRadius: 'md',
                                                    fontSize: 'sm',
                                                }}
                                            >
                                                {option.label}
                                            </Chip>
                                        )
                                    })
                                }
                            />
                        </Box>
                    </FormControl>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <FormControl size="lg">
                        <FormLabel>Search query:</FormLabel>
                        <Autocomplete
                            id="search-input"
                            placeholder="Search messages, authors, chats, etc."
                            multiple
                            freeSolo
                            options={[]}
                            value={searchTags}
                            sx={{
                                minWidth: '400px',
                            }}
                            onChange={(event, newValue) =>
                                setSearchTags(newValue)
                            }
                            inputValue={searchQuery}
                            onInputChange={(event, newInputValue) => {
                                setSearchQuery(newInputValue)
                            }}
                            onKeyDown={handleKeyDown}
                            renderTags={(tags, getTagProps) =>
                                tags.map((tag, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={index}
                                        variant="soft"
                                        color="primary"
                                        endDecorator={<Close />}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingRight: 0.5,
                                            gap: 0.5,
                                            borderRadius: 'md',
                                            fontSize: 'sm',
                                        }}
                                    >
                                        {tag}
                                    </Chip>
                                ))
                            }
                        />
                    </FormControl>
                </Box>
            </Box>

            <Box
                sx={{
                    marginTop: 4,
                }}
            >
                <MessageList
                    startDateTime={from}
                    endDateTime={to}
                    integrationList={integrationList}
                    messagesList={messagesList}
                    isLoading={isLoadingMessages}
                    isFetching={isFetching}
                    setFilters={setFilters}
                    workspaceId={workspaceId}
                    query={query}
                />
            </Box>
        </>
    )
}

export default MessagesPage
